import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Client } from '../../types/weighing';

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (clientData: Partial<Client>) => Promise<void>;
  client: Client;
}

interface ClientData {
  code_client: string;
  nom: string;
  type?: string;
  adresse?: string;
  telephone?: string;
  email?: string;
  secteur?: string;
  statut?: string;
}

const EditClientModal: React.FC<EditClientModalProps> = ({ isOpen, onClose, onSave, client }) => {
  const [formData, setFormData] = useState<ClientData>({
    code_client: '',
    nom: '',
    type: '',
    adresse: '',
    telephone: '',
    email: '',
    secteur: '',
    statut: 'actif'
  });

  const [errors, setErrors] = useState<Partial<ClientData>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        code_client: client.code_client || '',
        nom: client.nom || '',
        type: client.type || '',
        adresse: client.adresse || '',
        telephone: client.telephone || '',
        email: client.email || '',
        secteur: client.secteur || '',
        statut: client.statut || 'actif'
      });
    }
  }, [client]);

  const handleInputChange = (field: keyof ClientData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ClientData> = {};

    if (!formData.code_client.trim()) {
      newErrors.code_client = 'Le code client est requis';
    } else if (!/^[A-Z0-9\-_]+$/.test(formData.code_client)) {
      newErrors.code_client = 'Le code client ne peut contenir que des lettres, chiffres, tirets (-) et underscores (_)';
    } else if (formData.code_client.length < 2) {
      newErrors.code_client = 'Le code client doit contenir au moins 2 caractères';
    }
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        await onSave(formData);
        onClose();
      } catch (error: any) {
        console.error('Erreur lors de la modification du client:', error);
        
        // Gérer les erreurs d'unicité du code_client
        if (error.response?.data?.code_client) {
          setErrors({ code_client: 'Ce code client existe déjà. Veuillez en choisir un autre.' });
        } else if (error.response?.data?.non_field_errors) {
          setSubmitError(error.response.data.non_field_errors[0]);
        } else {
          setSubmitError('Erreur lors de la modification du client');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le client" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Code Client */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Code Client <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.code_client}
            onChange={(e) => handleInputChange('code_client', e.target.value.toUpperCase())}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.code_client ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="CLI-001 ou CLI_001"
          />
          {errors.code_client && (
            <p className="mt-1 text-sm text-red-600">{errors.code_client}</p>
          )}
        </div>

        {/* Nom et Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom du client"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type 
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.type ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un type</option>
              <option value="agro-industrie">Agro-industrie</option>
              <option value="commerce-international">Commerce international</option>
              <option value="distribution-locale">Distribution locale</option>
              <option value="export-specialise">Export spécialisé</option>
              <option value="import-export">Import-Export</option>
              <option value="particulier">Particulier</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type}</p>
            )}
          </div>
        </div>

        {/* Secteur */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Secteur d'activité
          </label>
          <select
            value={formData.secteur}
            onChange={(e) => handleInputChange('secteur', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Sélectionner un secteur</option>
            <option value="cereales">Céréales</option>
            <option value="legumineuses">Légumineuses</option>
            <option value="oleagineux">Oléoagineux</option>
            <option value="elevage">Élevage</option>
            <option value="transformation">Transformation alimentaire</option>
            <option value="distribution">Distribution</option>
            <option value="autre">Autre</option>
          </select>
        </div>

        {/* Adresse */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse
          </label>
          <textarea
            value={formData.adresse}
            onChange={(e) => handleInputChange('adresse', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="Adresse complète du client"
          />
        </div>

        {/* Téléphone et Email */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => handleInputChange('telephone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="01 23 45 67 89"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="client@email.com"
            />
          </div>
        </div>

        {/* Statut */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Statut
          </label>
          <select
            value={formData.statut}
            onChange={(e) => handleInputChange('statut', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="actif">Actif</option>
            <option value="inactif">Inactif</option>
          </select>
        </div>

        {/* Erreur de soumission */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                <div className="mt-2 text-sm text-red-700">{submitError}</div>
              </div>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSubmitting ? 'Modification...' : 'Modifier le client'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditClientModal;
